import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  sizes?: string;
  aspectRatio?: string;
  onLoad?: () => void;
}

// Generate a simple blur placeholder color from the image path
const getPlaceholderColor = (src: string): string => {
  // Create a simple hash from the src to generate consistent colors
  let hash = 0;
  for (let i = 0; i < src.length; i++) {
    hash = src.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate muted, pleasant colors
  const h = Math.abs(hash % 360);
  return `hsl(${h}, 20%, 75%)`;
};

export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  aspectRatio,
  onLoad
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "50px",
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  // Check if image is already cached
  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, [isInView]);

  const placeholderColor = getPlaceholderColor(src);

  // Generate srcset for external URLs (like Unsplash)
  const generateSrcSet = () => {
    if (src.includes("unsplash.com")) {
      const baseUrl = src.split("?")[0];
      return `${baseUrl}?w=400&fit=crop 400w, ${baseUrl}?w=800&fit=crop 800w, ${baseUrl}?w=1200&fit=crop 1200w`;
    }
    return undefined;
  };

  const srcSet = generateSrcSet();

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden",
        aspectRatio && `aspect-[${aspectRatio}]`
      )}
      style={{
        backgroundColor: placeholderColor,
        aspectRatio: aspectRatio || (width && height ? `${width}/${height}` : undefined)
      }}
    >
      {/* Blur placeholder */}
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-500",
          isLoaded ? "opacity-0" : "opacity-100"
        )}
        style={{
          background: `linear-gradient(135deg, ${placeholderColor}, ${getPlaceholderColor(src + "alt")})`,
          filter: "blur(20px)",
          transform: "scale(1.1)"
        }}
        aria-hidden="true"
      />

      {/* Actual image */}
      {isInView && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          width={width}
          height={height}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={handleLoad}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-500",
            isLoaded ? "opacity-100" : "opacity-0",
            className
          )}
        />
      )}
    </div>
  );
}
